import {
  Accented,
  Band,
  CtaArrow,
  CtaLink,
  ctaClasses,
  Display,
  Eyebrow,
  Lead,
} from '@/components/bedrock';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';

import { HeroMark } from './HeroMark';

import type { ReactElement } from 'react';

/**
 * The landing hero — centred, light, and deliberately quiet.
 *
 * The previous hero carried a live product mock beside the headline. This one
 * carries nothing but the mark and the type: the design moves the demo into the
 * story band immediately below, where it gets a full black stage and 1,618px of
 * room instead of competing with the headline for the fold. So the hero's whole
 * job is the sentence, and everything here serves reading it.
 *
 * It is the LCP element, which is why it renders no image, no canvas and no
 * deferred chunk — the headline paints with the document.
 *
 * ## The mark is the hero's GROUND, not an item in its stack
 *
 * In flow it was a second copy of the nav logo 130px below the first, and it
 * pushed the headline a quarter of the fold down a band whose whole job is the
 * sentence. Oversized and behind the type it reads as depth instead of as a
 * repeat, and the sentence gets the fold back.
 *
 * The mark centres on the WHOLE hero block rather than on the headline alone:
 * centred on the headline it reached above the band and was sliced by the
 * header bar, and light with a hard horizontal edge stops reading as light.
 * Centred here it clears the header and the band below, and it tracks the type
 * as copy reflows across locales — there is no offset to re-tune.
 *
 * Both children of the wrapper are positioned, so DOM order alone puts the type
 * on top — no z-index, and nothing to keep in step if a third layer ever lands
 * between them.
 */
export function Hero(): ReactElement {
  const { t } = useT();

  return (
    <Band
      as="header"
      id="top"
      space="none"
      className="max-w-[1000px] px-5 pb-[74px] pt-[104px] text-center sm:px-8"
      aria-labelledby="hero-title"
    >
      {/* `overflow-x-clip`: the mark is deliberately wider than the column it
          sits in (a light source that fits inside its own frame reads as a
          shape), and its aurora pools reach a further 14% beyond that. Its
          EDGE_FADE mask dissolves them visually but a mask does not clip
          LAYOUT — so at ~900px the pools reached 1,013px inside a 900px page
          and the whole landing scrolled sideways under a finger.

          `clip` rather than `hidden` because `hidden` on one axis makes the
          other axis `auto`, which would give the hero its own scroll container
          and break the sticky header's scroll context. `-x-` only, so nothing
          vertical is cut. */}
      <div className="relative overflow-x-clip">
        <HeroMark />

        <div className="relative">
          <Eyebrow dot>{t('home.hero.eyebrow')}</Eyebrow>

          <Display id="hero-title" className="mt-6">
            <Accented text={t('home.hero.title')} accent={t('home.hero.titleAccent')} />
          </Display>

          <Lead size="lg" className="mx-auto mt-7 max-w-[58ch]">
            {t('home.hero.lead')}
          </Lead>

          <div className="mt-9 flex flex-wrap justify-center gap-3">
            {/* An in-page jump, so it is a plain anchor rather than a router link —
                the router would push a history entry for a scroll. */}
            <CtaLink href="#story">
              {t('home.hero.ctaPrimary')}
              <CtaArrow />
            </CtaLink>
            <LocalizedLink to="/contact" className={ctaClasses({ variant: 'secondary' })}>
              {t('home.hero.ctaSecondary')}
            </LocalizedLink>
          </div>

          {/* `--bd-subtle`, not `--bd-faint`. The faint token is documented as
              the AA floor for text ON WHITE (5.05:1), which leaves it no
              headroom once the mark tints the ground behind it — measured
              4.40:1 against the mark's darkest point, under the 4.5 floor. One
              step darker restores it and keeps the strip quiet. The mark's
              aperture layer now clears this column too, so the real ratio is
              better than that; the darker token stays because the margin is
              what makes the strip robust to a future re-tune of the ground. */}
          <ul className="mt-11 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[13px] text-[color:var(--bd-subtle)]">
            {[t('home.hero.proof1'), t('home.hero.proof2'), t('home.hero.proof3')].map(
              (claim, i) => (
                <li key={claim} className="flex items-center gap-6">
                  {/* The separator belongs to the item that FOLLOWS it, so it never
                      trails the last one and never needs a nth-child rule. */}
                  {i > 0 ? (
                    <span aria-hidden="true" className="text-[color:var(--bd-subtle)]">
                      ·
                    </span>
                  ) : null}
                  {claim}
                </li>
              ),
            )}
          </ul>
        </div>
      </div>
    </Band>
  );
}
